import { makeAutoObservable, runInAction } from 'mobx'
import { InboxesApi } from 'entities/Inbox'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox.ts'
import { type IResponseBusinessHours } from 'entities/Users/api/types.ts'
import { MembersStore } from 'pages/settings/pages/inboxes/pages/id/store/membersStore'
import { InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'
import { type InboxesViewStore } from 'pages/settings/pages/inboxes/pages/inboxesView/store/inboxesViewStore'
import { SmartCallEditStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/edit/store/SmartCallEditStore.tsx'
import { NumbersStore } from './numbersStore'

export class InboxLayerStore {
  private _numbersStore: NumbersStore
  private _membersStore: MembersStore
  private _team: IResponseInbox | null = null
  private _inboxSettingsStore: InboxSettingsStore
  readonly smartCallEditStore

  constructor(
    private _inboxesViewStore: InboxesViewStore,
    private readonly _entities: IEntitiesStore,
    private id: number
  ) {
    this._membersStore = new MembersStore(this, this._entities)
    this._numbersStore = new NumbersStore(this, this._entities)
    this._inboxSettingsStore = new InboxSettingsStore(this, this._entities)
    this.smartCallEditStore = new SmartCallEditStore(_entities, id)

    makeAutoObservable(this)
  }

  get numbersStore() {
    return this._numbersStore
  }

  get membersStore() {
    return this._membersStore
  }

  get inboxesViewStore() {
    return this._inboxesViewStore
  }

  get entities() {
    return this._entities
  }

  get currentTeam() {
    return this._team
  }
  get currentEntityInbox(): Inbox | undefined {
    const inbox = this.entities.inboxesStore.getItem(this.id)
    if (inbox instanceof Inbox) {
      return inbox
    }
  }

  get inboxId() {
    return this._team?.id || 0
  }

  get inboxSettingsStore() {
    return this._inboxSettingsStore
  }

  get isAircallOnlyTeam() {
    return this._team?.numbers.every((number) => number.is_aircall)
  }

  get isCallFlowEnabled() {
    return this.smartCallEditStore.hasActiveCallFlow
  }

  updateCurrentTeamName = (name: string) => {
    if (!this._team) return
    this._team.name = name
  }
  updateCurrentTeamBusinessHours = (business_hours: IResponseBusinessHours) => {
    if (!this._team) return
    this._team.business_hours = business_hours
    const inbox = this.entities.inboxesStore.getItem(this._team.id)
    if (inbox instanceof Inbox) {
      inbox.updateBusinessHours(business_hours)
    }
  }

  init = async (inboxId: number) => {
    const team = await this.loadTeam(inboxId)

    if (team) {
      await Promise.all([
        this._membersStore.init(inboxId),
        this.numbersStore.init(inboxId),
        this._inboxSettingsStore.init(team),
      ])
    }
  }
  initOnMount = () => {
    this.smartCallEditStore.init()
  }

  loadTeam = async (inboxId: number) => {
    try {
      const response = await InboxesApi.getTeamById(inboxId)
      const team = response.data

      this._entities.inboxesStore.addItem(team)

      runInAction(() => {
        this._team = team
      })

      return team
    } catch (error) {
      console.error(error)
    }
  }

  dispose = () => {
    this._membersStore.dispose()
    this._numbersStore.dispose()
    this._inboxSettingsStore.dispose()
    this.smartCallEditStore.dispose()
  }

  get isAutoRechargeRequired() {
    return this.smartCallEditStore.isAutoRechargeRequired
  }
}
